/** Banc d’essai sans écriture dans le fil et mesures métier sur trente jours. */
import { useState } from 'react';
import { Link } from 'react-router';
import { useTranslation } from 'react-i18next';
import { Button, Input, Select, Textarea } from '@artosera/ui';
import { assistantApi, useLectureAssistant } from '../../api/assistant-reponse.js';
import { useConversations } from '../../api/boite-reception.js';
import { galerieALaction, galerieAModule } from '../../auth/sujet.js';
import { useShellContext } from '../shell/context.js';
import { NotFoundPage } from '../introuvable/NotFoundPage.js';
import { Field } from '../Field.js';
import { useDebounce } from '../oeuvres/useDebounce.js';
import { NavigationBoite } from './NavigationBoite.js';
import chiffresStyles from '../pipeline/Pipeline.module.css';
import { useGesteAssistant } from './useGesteAssistant.js';
import styles from './Boite.module.css';
import assistantStyles from './Assistant.module.css';

export function AssistantPage() {
  const { galerie, galerieSlug, compte } = useShellContext();

  if (!galerieAModule(galerie, 'assistant-reponse') || !galerieALaction(galerie, 'inbox:settings'))
    return <NotFoundPage />;

  return <Assistant key={`${compte.id}:${galerieSlug}`} />;
}

function Assistant() {
  const { galerieSlug, galerie } = useShellContext();

  const { t, i18n } = useTranslation(['regie', 'commun']);

  const [mode, setMode] = useState('texte');

  const [texte, setTexte] = useState('');

  const [conversationId, setConversationId] = useState('');

  const [recherche, setRecherche] = useState('');

  const [page, setPage] = useState(1);

  const q = useDebounce(recherche);

  const conversations = useConversations(
    galerieSlug,
    { vue: 'toutes', q, page },
    mode === 'conversation' && galerieALaction(galerie, 'inbox:read'),
  );

  const essai = useGesteAssistant((corps: { texte: string } | { conversationId: string }, appel) =>
    assistantApi.essai(galerieSlug, corps, appel),
  );

  const [resultat, setResultat] = useState<Awaited<ReturnType<typeof assistantApi.essai>> | null>(
    null,
  );

  const tableau = useLectureAssistant(galerieSlug, 'tableau', (appel) =>
    assistantApi.tableau(galerieSlug, appel),
  );

  return (
    <section className={styles.page}>
      <h1>{t('regie:assistantReponse.assistant')}</h1>
      <NavigationBoite courante="assistant" />
      <nav className={assistantStyles.navigation}>
        {galerieALaction(galerie, 'ia:read') && (
          <Link to={`/${galerieSlug}/reglages/ia`}>{t('regie:ia.titre')}</Link>
        )}
      </nav>
      <section className={styles.bloc}>
        <h2>{t('regie:assistantReponse.essai')}</h2>
        <p>{t('regie:assistantReponse.cout')}</p>
        <form
          className={styles.formulaire}
          onSubmit={(e) => {
            e.preventDefault();
            if (essai.pending || (mode === 'texte' ? !texte.trim() : !conversationId)) return;
            setResultat(null);
            void essai.executer(mode === 'texte' ? { texte } : { conversationId }).then((r) => {
              if (r) setResultat(r);
            });
          }}
        >
          <Field htmlFor="assistant-mode" label={t('regie:assistantReponse.sourceEssai')}>
            <Select
              id="assistant-mode"
              value={mode}
              onValueChange={(v) => {
                setMode(v);
                setResultat(null);
              }}
              options={[
                { value: 'texte', label: t('regie:assistantReponse.messageEssai') },
                ...(galerieALaction(galerie, 'inbox:read')
                  ? [
                      {
                        value: 'conversation',
                        label: t('regie:assistantReponse.conversationEssai'),
                      },
                    ]
                  : []),
              ]}
            />
          </Field>
          {mode === 'texte' ? (
            <Field htmlFor="assistant-texte" label={t('regie:assistantReponse.messageEssai')}>
              <Textarea
                id="assistant-texte"
                required
                maxLength={4000}
                value={texte}
                onChange={(e) => setTexte(e.target.value)}
                aria-describedby="assistant-borne"
              />
              <small id="assistant-borne">{texte.length} / 4 000</small>
            </Field>
          ) : (
            <>
              <Field htmlFor="assistant-recherche" label={t('regie:boiteReception.recherche')}>
                <Input
                  id="assistant-recherche"
                  type="search"
                  maxLength={200}
                  value={recherche}
                  onChange={(e) => {
                    setRecherche(e.target.value);
                    setPage(1);
                    setConversationId('');
                  }}
                />
              </Field>
              {conversations.isPending && <p role="status">{t('commun:chargement')}</p>}
              {conversations.isError && <p role="alert">{t('commun:erreurReseau')}</p>}
              <Field
                htmlFor="assistant-choix"
                label={t('regie:assistantReponse.conversationEssai')}
              >
                <Select
                  id="assistant-choix"
                  value={conversationId}
                  onValueChange={setConversationId}
                  options={[
                    { value: '', label: t('regie:boiteReception.selectionner') },
                    ...(conversations.data?.conversations.map((c) => ({
                      value: c.id,
                      label: `${c.correspondant.nom || c.correspondant.adresse} · ${c.sujet}`,
                    })) ?? []),
                  ]}
                />
              </Field>
              <div className={styles.gestes}>
                <Button
                  type="button"
                  variant="secondary"
                  disabled={page <= 1}
                  onClick={() => {
                    setPage(page - 1);
                    setConversationId('');
                  }}
                >
                  {t('regie:boiteReception.precedent')}
                </Button>
                <Button
                  type="button"
                  variant="secondary"
                  disabled={!conversations.data || page >= conversations.data.pagination.pages}
                  onClick={() => {
                    setPage(page + 1);
                    setConversationId('');
                  }}
                >
                  {t('regie:boiteReception.suivant')}
                </Button>
              </div>
            </>
          )}
          <Button
            type="submit"
            disabled={essai.pending || (mode === 'texte' ? !texte.trim() : !conversationId)}
          >
            {t('regie:ia.essayer')}
          </Button>
        </form>
        {essai.pending && <p role="status">{t('regie:assistantReponse.attente')}</p>}
        {essai.erreur && <p role="alert">{essai.erreur}</p>}
        {resultat && (
          <section className={styles.texte} aria-label={t('regie:assistantReponse.resultat')}>
            <h3>{t('regie:assistantReponse.resultat')}</h3>
            <p>
              {resultat.tri.categorie === 'indesirable'
                ? t('regie:assistantReponse.indesirable')
                : t(`regie:boiteReception.categories.${resultat.tri.categorie}`)}
            </p>
            <p>
              {t('regie:assistantReponse.urgence')} :{' '}
              {t(`regie:assistantReponse.urgences.${resultat.tri.urgence}`)}
            </p>
            <p>{resultat.tri.resume}</p>
            <h4>{t('regie:assistantReponse.gardeFous')}</h4>
            <p>
              {t(
                resultat.sensible
                  ? 'regie:assistantReponse.sensible'
                  : 'regie:assistantReponse.nonSensible',
              )}
            </p>
            <ul>
              {resultat.motifsSensibles.map((m, i) => (
                <li key={i}>
                  {t(`regie:assistantReponse.motifs.${m}`, {
                    defaultValue: t('regie:assistantReponse.motifs.assistant'),
                  })}
                </li>
              ))}
            </ul>
            {resultat.brouillon ? (
              <>
                <p>{t('regie:assistantReponse.prepare')}</p>
                <p>{resultat.brouillon.texte}</p>
                <h4>{t('regie:assistantReponse.aVerifier')}</h4>
                <ul>
                  {resultat.brouillon.aVerifier.map((v, i) => (
                    <li key={i}>{v}</li>
                  ))}
                </ul>
              </>
            ) : (
              <p>{t('regie:assistantReponse.sensibleAide')}</p>
            )}
          </section>
        )}
      </section>
      <section className={styles.bloc}>
        <h2>{t('regie:assistantReponse.tableau')}</h2>
        {tableau.isPending && <p role="status">{t('commun:chargement')}</p>}
        {tableau.isError && <p role="alert">{t('commun:erreurReseau')}</p>}
        {tableau.data && (
          <>
            <dl className={chiffresStyles.chiffres}>
              {(
                [
                  'messagesTries',
                  'brouillonsPrepares',
                  'partSansModification',
                  'reponsesAutomatiques',
                  'conversationsSensibles',
                ] as const
              ).map((cle) => (
                <div key={cle}>
                  <dt>{t(`regie:assistantReponse.mesures.${cle}`)}</dt>
                  <dd>
                    {cle === 'partSansModification'
                      ? tableau.data[cle] === null
                        ? '—'
                        : new Intl.NumberFormat(i18n.language, {
                            style: 'percent',
                            maximumFractionDigits: 1,
                          }).format(tableau.data[cle])
                      : tableau.data[cle]}
                  </dd>
                </div>
              ))}
            </dl>
            <h3>{t('regie:assistantReponse.echecs')}</h3>
            {Object.keys(tableau.data.echecs).length === 0 && (
              <p>{t('regie:assistantReponse.aucunEchec')}</p>
            )}
            <dl className={chiffresStyles.chiffres}>
              {Object.entries(tableau.data.echecs).map(([cause, n]) => (
                <div key={cause}>
                  <dt>{t(`regie:ia.etats.${cause}`, { defaultValue: cause })}</dt>
                  <dd>{n}</dd>
                </div>
              ))}
            </dl>
          </>
        )}
      </section>
    </section>
  );
}
